import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useWorkObjects } from '../../hooks/useWorkObjects';

type WorkObject = {
  id: string;
  title: string;
  status: 'Backlog' | 'In Progress' | 'Blocked' | 'Review' | 'Done';
  assignees: string[];
  labels?: string[];
  createdAt: string;
  updatedAt: string;
  description?: string;
  startAt?: string;
  dueAt?: string;
};

interface KanbanColumnProps {
  title: string;
  status: WorkObject['status'];
  items: WorkObject[];
  isDraggingOver?: boolean;
  children?: React.ReactNode;
}

const KanbanColumn = React.forwardRef<HTMLDivElement, KanbanColumnProps & any>(
  ({ title, status, items, isDraggingOver, children, ...provided }, ref) => {
    return (
      <div 
        ref={ref}
        {...provided}
        className={`beautiful-kanban-column bg-gradient-to-br from-white to-gray-50 rounded-xl p-8 min-h-[500px] border border-gray-200 shadow-lg min-w-[280px] transition-all duration-200 ${
          isDraggingOver ? 'border-[#4267A1] bg-blue-50/50' : ''
        }`}
        style={{
          boxShadow: '0 8px 32px rgba(66, 103, 161, 0.1), 0 0 0 1px rgba(66, 103, 161, 0.05) inset'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-gray-800 font-semibold text-lg tracking-wide drop-shadow-sm">{title}</h3>
          <div className="bg-gradient-to-r from-[#4267A1]/20 to-[#5A7BC4]/20 text-[#4267A1] text-xs px-3 py-1 rounded-full border border-[#4267A1]/30">
            {items.length}
          </div>
        </div>
        
        <div className="space-y-4">
          {items.map((item, index) => (
            <Draggable key={item.id} draggableId={item.id} index={index}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  className={`beautiful-task-card bg-gradient-to-br from-gray-50 to-white p-6 rounded-lg cursor-move border border-gray-200 transition-all duration-300 hover:border-[#4267A1]/50 hover:shadow-lg hover:shadow-[#4267A1]/20 min-h-[120px] ${
                    snapshot.isDragging ? 'rotate-2 shadow-2xl scale-105' : ''
                  }`}
                  style={{
                    boxShadow: '0 4px 16px rgba(66, 103, 161, 0.1), 0 0 0 1px rgba(66, 103, 161, 0.05) inset',
                    ...provided.draggableProps.style
                  }}
                >
                  <h4 className="text-gray-800 font-medium mb-2 text-sm leading-relaxed drop-shadow-sm">{item.title}</h4>
                  {item.description && (
                    <p className="text-gray-600 text-xs mb-3 leading-relaxed opacity-90 drop-shadow-sm">{item.description}</p>
                  )}
                  {item.assignees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {item.assignees.map((assignee) => (
                        <span key={assignee} className="bg-gradient-to-r from-[#4267A1]/20 to-[#5A7BC4]/20 text-[#4267A1] text-xs px-3 py-1 rounded-full border border-[#4267A1]/30 font-medium">
                          {assignee}
                        </span>
                      ))}
                    </div>
                  )}
                  {item.labels && item.labels.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {item.labels.map((label) => (
                        <span key={label} className="bg-gradient-to-r from-[#8A9150]/20 to-[#9BA55F]/20 text-[#8A9150] text-xs px-3 py-1 rounded-full border border-[#8A9150]/30 font-medium">
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Draggable>
          ))}
          {items.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <p className="text-sm">Drop items here</p>
            </div>
          )}
          {children}
        </div>
      </div>
    );
  }
);

const demoWorkObjects: WorkObject[] = [
  {
    id: 'demo-1',
    title: 'Setup Tyler CRM Integration',
    description: 'Configure API connections and data mapping for Tyler CRM system',
    status: 'Backlog',
    assignees: ['Tyler PM', 'Integration Team'],
    labels: ['High Priority', 'Integration'],
    createdAt: '2025-08-20T10:00:00Z',
    updatedAt: '2025-08-22T14:30:00Z',
    dueAt: '2025-09-15T17:00:00Z'
  },
  {
    id: 'demo-2',
    title: 'User Training Materials',
    description: 'Create comprehensive training documentation and video tutorials',
    status: 'In Progress',
    assignees: ['Training Team'],
    labels: ['Documentation', 'Training'],
    createdAt: '2025-08-18T09:00:00Z',
    updatedAt: '2025-08-24T11:15:00Z',
    dueAt: '2025-09-10T16:00:00Z'
  },
  {
    id: 'demo-3',
    title: 'Data Migration Planning',
    description: 'Plan and schedule data migration from legacy systems',
    status: 'In Progress',
    assignees: ['Data Team', 'Tyler PM'],
    labels: ['Critical', 'Migration'],
    createdAt: '2025-08-15T08:00:00Z',
    updatedAt: '2025-08-24T13:45:00Z',
    dueAt: '2025-09-08T18:00:00Z'
  },
  {
    id: 'demo-4',
    title: 'Security Audit Review',
    description: 'Complete security review and compliance verification',
    status: 'Blocked',
    assignees: ['Security Team'],
    labels: ['Security', 'Compliance'],
    createdAt: '2025-08-19T11:00:00Z',
    updatedAt: '2025-08-23T16:20:00Z',
    dueAt: '2025-09-12T17:00:00Z'
  },
  {
    id: 'demo-5',
    title: 'Client Acceptance Testing',
    description: 'Coordinate user acceptance testing with client stakeholders',
    status: 'Review',
    assignees: ['QA Team', 'Client Lead'],
    labels: ['Testing', 'Client'],
    createdAt: '2025-08-21T12:00:00Z',
    updatedAt: '2025-08-24T10:30:00Z',
    dueAt: '2025-09-20T15:00:00Z'
  },
  {
    id: 'demo-6',
    title: 'Go-Live Preparation',
    description: 'Final preparations and deployment checklist completion',
    status: 'Done',
    assignees: ['DevOps Team'],
    labels: ['Deployment', 'Complete'],
    createdAt: '2025-08-10T07:00:00Z',
    updatedAt: '2025-08-30T18:00:00Z',
    dueAt: '2025-08-30T23:59:00Z'
  },
  {
    id: 'demo-7',
    title: 'System Performance Testing',
    description: 'Load testing and performance optimization',
    status: 'Done',
    assignees: ['Performance Team'],
    labels: ['Testing', 'Performance'],
    createdAt: '2025-08-12T09:30:00Z',
    updatedAt: '2025-08-25T17:45:00Z',
    dueAt: '2025-08-25T20:00:00Z'
  },
  {
    id: 'demo-8',
    title: 'Configure Single Sign-On',
    description: 'Set up SSO integration with Active Directory and SAML',
    status: 'Backlog',
    assignees: ['Security Team', 'IT Admin'],
    labels: ['Security', 'Authentication'],
    createdAt: '2025-08-22T14:00:00Z',
    updatedAt: '2025-08-24T09:15:00Z',
    dueAt: '2025-09-18T16:00:00Z'
  }
];

export const WorkKanban: React.FC = () => {
  const [fallbackMode, setFallbackMode] = useState(false);
  const [localData, setLocalData] = useState({ items: demoWorkObjects, loading: false });

  let hookData, hookLoading, hookUpdateWorkObject;
  try {
    const hookResult = useWorkObjects();
    hookData = hookResult.data;
    hookLoading = hookResult.loading;
    hookUpdateWorkObject = hookResult.updateWorkObject;
  } catch (error) {
    console.warn('Work objects API unavailable, using demo mode:', error);
    setFallbackMode(true);
  }

  const data = fallbackMode ? localData : (hookData || { items: [] });
  const loading = fallbackMode ? localData.loading : (hookLoading || false);

  const [workItems, setWorkItems] = useState<WorkObject[]>(() => 
    data.items.length === 0 ? demoWorkObjects : data.items
  );

  const handleDragEnd = (result: any) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStatus = destination.droppableId as WorkObject['status'];
    
    if (fallbackMode || data.items.length === 0) {
      const updatedItems = workItems.map(item => 
        item.id === draggableId ? { ...item, status: newStatus, updatedAt: new Date().toISOString() } : item
      );
      setWorkItems(updatedItems);
      setLocalData(prev => ({ ...prev, items: updatedItems }));
      console.log(`Demo mode: Moved item ${draggableId} to ${newStatus}`);
      return;
    }

    hookUpdateWorkObject(draggableId, { status: newStatus }).then(() => {
      console.log(`Moved work object ${draggableId} to ${newStatus}`);
    }).catch((error) => {
      console.error('Failed to move work object:', error);
      setFallbackMode(true);
    });
  };

  const columns = [
    { title: 'Backlog', status: 'Backlog' as const },
    { title: 'In Progress', status: 'In Progress' as const },
    { title: 'Blocked', status: 'Blocked' as const },
    { title: 'Review', status: 'Review' as const },
    { title: 'Done', status: 'Done' as const }
  ];

  if (loading) {
    return (
      <div className="beautiful-kanban flex items-center justify-center h-64 bg-gradient-to-br from-white via-gray-50 to-white">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#4267A1]/20 to-[#5A7BC4]/20 flex items-center justify-center border border-[#4267A1]/30 animate-pulse">
            <svg className="w-6 h-6 text-[#4267A1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </div>
          <p className="text-gray-700 font-medium">Loading beautiful Kanban board...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="beautiful-kanban p-8 bg-gradient-to-br from-white via-gray-50 to-white min-h-screen">
      <div className="mb-8">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-[#4267A1] via-[#5A7BC4] to-[#4267A1] bg-clip-text text-transparent mb-2 drop-shadow-sm">
          Work Items Kanban
        </h2>
        <p className="text-gray-600 text-lg font-medium drop-shadow-sm">Drag and drop to organize your workflow</p>
        
        {(fallbackMode || data.items.length === 0) && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700 text-sm">
              ℹ️ Displaying demo work items. Drag and drop functionality is fully interactive in demo mode.
            </p>
          </div>
        )}
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="kanban-grid w-full space-y-6">
          {/* Top row: 3 columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {columns.slice(0, 3).map((column) => (
              <Droppable key={column.status} droppableId={column.status}>
                {(provided, snapshot) => (
                  <KanbanColumn
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    title={column.title}
                    status={column.status}
                    items={workItems.filter(item => item.status === column.status)}
                    isDraggingOver={snapshot.isDraggingOver}
                  >
                    {provided.placeholder}
                  </KanbanColumn>
                )}
              </Droppable>
            ))}
          </div>

          {/* Bottom row: 2 columns centered */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {columns.slice(3).map((column) => (
              <Droppable key={column.status} droppableId={column.status}>
                {(provided, snapshot) => (
                  <KanbanColumn
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    title={column.title}
                    status={column.status}
                    items={workItems.filter(item => item.status === column.status)}
                    isDraggingOver={snapshot.isDraggingOver}
                  >
                    {provided.placeholder}
                  </KanbanColumn>
                )}
              </Droppable>
            ))}
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};
